import {
  getName,
  NcBadge,
  NcButton,
  NcDisclosure,
  NcFormattedMessage,
  NcFormattedName,
  NcGridList,
  NcIconEllipsis,
  NcLink,
  NcMenu,
  NcPaginator,
  NcPanel,
} from "@noted/noted-components";
import { useQuery } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";
import { ReactNode, useId, useState } from "react";

import { FeaturePermission } from "~/api/graphql-types";
import {
  Reminder,
  reminderV1Query,
  reminderV2Queries,
  useCompleteReminderMutation,
  useDeleteReminderMutation,
  useUpdateReminderMutation,
} from "~/api/reminders";
import { canDeleteClientRecords, useAccount } from "~/components/account-context/account.context";
import { ConfirmActionModal } from "~/components/actions-modal/confirm-actions-modal";
import { useFeatures } from "~/components/features.context";
import { useI18n } from "~/hooks/use-i18n";

import { NoRemindersIcon } from "./icons";
import RemindersEditModal from "./reminders-edit-modal";

const PanelEmpty = () => {
  const { t } = useI18n("org");
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8">
      <NoRemindersIcon opacity="0.5" width="8rem" />
      <NcFormattedMessage variant="secondary">
        {t("org:dashboard.reminders.empty")}
      </NcFormattedMessage>
    </div>
  );
};

function getMonthNameAbbreviation(dueDate: string) {
  return DateTime.fromISO(dueDate).toFormat("MMM").toLocaleUpperCase();
}

function getDateDay(dueDate: string) {
  return DateTime.fromISO(dueDate).toFormat("dd");
}

const ReminderItem = ({
  reminder,
  setEditingReminder,
  handleReminderDelete,
}: {
  reminder: Reminder;
  status: ReactNode;
  setEditingReminder: (r: Reminder) => void;
  handleReminderDelete: (r: Reminder) => void;
}) => {
  const { t } = useI18n("org");
  const { account, organisation } = useAccount();

  const { mutate: completeReminder } = useCompleteReminderMutation();

  return (
    <NcGridList.Item
      className="group grid grid-cols-[4.5rem_1fr_auto] items-start gap-3"
      textValue={`${getName(reminder.patient, "full")} ${reminder.title}`}
    >
      <div className="grid gap-1">
        <ReminderStatus dueDate={reminder.dueDate} />
        <div className="text-center text-sm">
          {getDateDay(reminder.dueDate)} {getMonthNameAbbreviation(reminder.dueDate)}
        </div>
      </div>

      <div className="grid gap-2">
        <NcLink
          className="group-hover:text-current group-focus:text-current"
          href={`/n/clients/${reminder.patientId}`}
        >
          <NcFormattedName name={reminder.patient} variant="full" />
        </NcLink>
        <NcDisclosure>
          <NcDisclosure.Trigger>{reminder.title}</NcDisclosure.Trigger>
          <NcDisclosure.Body>
            {reminder.description ? (
              reminder.description
            ) : (
              <NcFormattedMessage variant="secondary">
                {t("org:dashboard.reminders.no_description")}
              </NcFormattedMessage>
            )}
          </NcDisclosure.Body>
        </NcDisclosure>
      </div>
      <NcMenu.Trigger>
        <NcButton aria-label={t("org:dashboard.reminders.actions.trigger")}>
          <NcIconEllipsis />
        </NcButton>
        <NcMenu aria-label={t("org:dashboard.reminders.actions.label")} placement="bottom end">
          {!organisation.readOnlyMode && (
            <NcMenu.Item variant="success" onAction={() => completeReminder({ id: reminder.id })}>
              {t("org:dashboard.reminders.actions.complete.label")}
            </NcMenu.Item>
          )}
          <NcMenu.Item href={`/n/clients/${reminder.patientId}`}>
            {t("org:dashboard.reminders.actions.open_client")}
          </NcMenu.Item>
          {!organisation.readOnlyMode && (
            <>
              <NcMenu.Item onAction={() => setEditingReminder(reminder)}>
                {t("org:dashboard.reminders.actions.edit")}
              </NcMenu.Item>

              {canDeleteClientRecords(account) && (
                <>
                  <NcMenu.Separator />
                  <NcMenu.Item variant="danger" onAction={() => handleReminderDelete(reminder)}>
                    {t("org:dashboard.reminders.actions.delete")}
                  </NcMenu.Item>
                </>
              )}
            </>
          )}
        </NcMenu>
      </NcMenu.Trigger>
    </NcGridList.Item>
  );
};

const ReminderStatus = ({ dueDate }: { dueDate: string }) => {
  const { t } = useI18n("org");
  const today = DateTime.now().endOf("day");
  const dueDateEnd = DateTime.fromISO(dueDate).endOf("day");

  if (dueDateEnd < today) {
    return <NcBadge variant="danger">{t("org:dashboard.reminders.overdue")}</NcBadge>;
  } else if (dueDateEnd.equals(today)) {
    return <NcBadge>{t("org:dashboard.reminders.today")}</NcBadge>;
  } else {
    return <NcBadge variant="neutral">{t("org:dashboard.reminders.future")}</NcBadge>;
  }
};

const RemindersPanelV2 = () => {
  const { t } = useI18n("org");
  const { account } = useAccount();
  const panelId = useId();
  const [editingReminder, setEditingReminder] = useState<Reminder>();
  const [deletingReminder, setDeletingReminder] = useState<Reminder>();
  const [page, setPage] = useState(1);

  const { mutate: saveReminder } = useUpdateReminderMutation();
  const { mutate: deleteReminder, isPending: isDeleting } = useDeleteReminderMutation();
  const { data, isLoading, isFetching, error } = useQuery(
    reminderV2Queries.list({
      userId: account.id,
      state: "ACTIVE",
      page,
    })
  );

  const reminders = (data?.results || []).map(reminder => ({
    ...reminder,
    patientId: reminder.patient.id,
  }));

  const handleReminderSave = (reminder: Reminder) => {
    setEditingReminder(undefined);
    saveReminder(reminder);
  };

  const handleReminderDelete = (reminder: Reminder) => {
    setDeletingReminder(reminder);
  };

  return (
    <NcPanel>
      <NcPanel.Header>
        <NcPanel.Heading id={panelId} level={2}>
          {t("org:dashboard.reminders.heading")}
        </NcPanel.Heading>
        <NcBadge className="ml-auto">{data?.totalResults}</NcBadge>
      </NcPanel.Header>
      <NcPanel.Body
        className="p-0"
        isLoading={isLoading}
        error={
          error
            ? t("common:error_loading_refresh", {
                component: t("org:dashboard.reminders.name"),
              })
            : undefined
        }
      >
        {!reminders?.length && <PanelEmpty />}
        {!!reminders?.length && (
          <NcGridList aria-labelledby={panelId}>
            {reminders.map(reminder => (
              <ReminderItem
                key={reminder.id}
                reminder={reminder}
                status={<NcBadge variant="danger">{t("org:dashboard.reminders.overdue")}</NcBadge>}
                {...{
                  setEditingReminder,
                  handleReminderDelete,
                }}
              />
            ))}
          </NcGridList>
        )}
        {editingReminder && (
          <RemindersEditModal
            reminder={editingReminder}
            handleExit={() => setEditingReminder(undefined)}
            onSaveReminder={handleReminderSave}
          />
        )}
        {deletingReminder && (
          <ConfirmActionModal
            title={t("org:dashboard.reminders.delete.heading")}
            message={t("org:dashboard.reminders.delete.message")}
            continueLabel={t("org:dashboard.reminders.actions.delete")}
            inProgress={isDeleting}
            onCancel={() => setDeletingReminder(undefined)}
            onContinue={() => {
              deleteReminder({ id: deletingReminder.id });
              setDeletingReminder(undefined);
            }}
            variant="danger"
          />
        )}
      </NcPanel.Body>
      {data && data.totalPages > 1 && (
        <NcPanel.Footer>
          <NcPaginator
            maxCurrentAdjacentPages={1}
            currentPage={data.currentPageNumber}
            totalPages={data.totalPages}
            onPageChange={page => setPage(page)}
            isDisabled={isFetching}
          />
        </NcPanel.Footer>
      )}
    </NcPanel>
  );
};

// TODO: Remove this when TAC is fully rolled out
const RemindersPanelV1 = () => {
  const { t } = useI18n("org");
  const panelId = useId();
  const [editingReminder, setEditingReminder] = useState<Reminder>();
  const [deletingReminder, setDeletingReminder] = useState<Reminder>();

  const { mutate: saveReminder } = useUpdateReminderMutation();
  const { mutate: deleteReminder, isPending: isDeleting } = useDeleteReminderMutation();
  const { data, isLoading, error } = useQuery(reminderV1Query);

  const reminders = orderBy(data || [], "dueDate");

  const handleReminderSave = (reminder: Reminder) => {
    setEditingReminder(undefined);
    saveReminder(reminder);
  };

  const handleReminderDelete = (reminder: Reminder) => {
    setDeletingReminder(reminder);
  };

  return (
    <NcPanel>
      <NcPanel.Header>
        <NcPanel.Heading id={panelId} level={2}>
          {t("org:dashboard.reminders.heading")}
        </NcPanel.Heading>
        <NcBadge className="ml-auto">{reminders?.length}</NcBadge>
      </NcPanel.Header>
      <NcPanel.Body
        className="p-0"
        isLoading={isLoading}
        error={
          error
            ? t("common:error_loading_refresh", {
                component: t("org:dashboard.reminders.name"),
              })
            : undefined
        }
      >
        {!reminders?.length && <PanelEmpty />}
        {!!reminders?.length && (
          <NcGridList aria-labelledby={panelId}>
            {reminders.map(reminder => (
              <ReminderItem
                key={reminder.id}
                reminder={reminder}
                status={<NcBadge variant="danger">{t("org:dashboard.reminders.overdue")}</NcBadge>}
                {...{
                  setEditingReminder,
                  handleReminderDelete,
                }}
              />
            ))}
          </NcGridList>
        )}
        {editingReminder && (
          <RemindersEditModal
            reminder={editingReminder}
            handleExit={() => setEditingReminder(undefined)}
            onSaveReminder={handleReminderSave}
          />
        )}
        {deletingReminder && (
          <ConfirmActionModal
            title={t("org:dashboard.reminders.delete.heading")}
            message={t("org:dashboard.reminders.delete.message")}
            continueLabel={t("org:dashboard.reminders.actions.delete")}
            inProgress={isDeleting}
            onCancel={() => setDeletingReminder(undefined)}
            onContinue={() => {
              deleteReminder({ id: deletingReminder.id });
              setDeletingReminder(undefined);
            }}
            variant="danger"
          />
        )}
      </NcPanel.Body>
    </NcPanel>
  );
};

const RemindersPanel = () => {
  const { featureEnabled } = useFeatures();
  const tacFeatureEnabled = featureEnabled(FeaturePermission.TeamAccessControls);
  return tacFeatureEnabled ? <RemindersPanelV2 /> : <RemindersPanelV1 />;
};

export default RemindersPanel;
